/**
 * «Не прошло — как выйти»: одна нога стоит, вторая не встала.
 *
 * Практик (28.09): «в спешке не понимаю, как с минимальным убытком выйти из
 * сделки». Здесь каждый выход посчитан в обоих исходах по живому стакану и
 * котировкам других контор, с комиссией taker; лучший — тот, у кого худший
 * исход выше. Все суммы в долларах, исход A — тот, на который куплены шары
 * Polymarket, B — исход ноги конторы.
 */

import { Decimal } from 'decimal.js';

import { type Odds, SPORTS_FEE_RATE, takerFeePerShare } from './calc';

/** Уровень стакана `[цена, шары]`, как в `/book` CLOB. */
export type Level = readonly [string, string];

export type Stand =
  /** Стоят шары Polymarket на исход A: сколько куплено и сколько за них отдано. */
  | { kind: 'shares'; shares: Odds; cost: Odds }
  /** Стоит ставка в конторе на исход B. */
  | { kind: 'bet'; source: string; stake: Odds; odds: Odds };

export interface ExitInput {
  stand: Stand;
  /** Покупатели шаров A — по ним продаём. */
  bids?: readonly Level[];
  /** Продавцы шаров A — по ним докупаем Polymarket, когда стоит контора. */
  asks?: readonly Level[];
  /** Продавцы шаров B (второй токен того же рынка) — перекрытие на самой бирже. */
  oppAsks?: readonly Level[];
  /** Котировки контор на недостающий исход: B, если стоят шары; A, если стоит ставка. */
  covers?: readonly { source: string; odds: Odds }[];
  /** Контора ноги, которая не встала, и её кэф сейчас — «доставить» там же. */
  retry?: { source: string; odds: Odds };
  feeRate?: Odds;
}

export type ExitKind = 'sell' | 'cover' | 'rebet' | 'buy' | 'buyOpp';

export interface ExitWay {
  kind: ExitKind;
  /** Площадка, где делается выход. */
  source: string;
  /** Шары (sell / buy / buyOpp) или ставка в долларах (cover / rebet). */
  amount: Decimal;
  /** Средняя цена шара или кэф конторы. */
  price: Decimal;
  /** Итог позиции целиком, если выиграет исход A / B. */
  ifA: Decimal;
  ifB: Decimal;
  worst: Decimal;
  /** Стакан не вместил весь объём — выход частичный. */
  partial: boolean;
}

const D = (v: Odds): Decimal => (v instanceof Decimal ? v : new Decimal(v));
const ZERO = new Decimal(0);
const money = (v: Decimal): Decimal => v.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

function way(
  kind: ExitKind,
  source: string,
  amount: Decimal,
  price: Decimal,
  ifA: Decimal,
  ifB: Decimal,
  partial: boolean,
): ExitWay {
  const a = money(ifA);
  const b = money(ifB);
  return { kind, source, amount, price, ifA: a, ifB: b, worst: Decimal.min(a, b), partial };
}

/** Продать `shares` по покупателям: выручка за вычетом комиссии и сколько продано. */
export function sellInto(
  bids: readonly Level[],
  shares: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
): { sold: Decimal; cash: Decimal } {
  const levels = [...bids]
    .map(([px, size]) => [D(px), D(size)] as const)
    .filter(([px, size]) => px.gt(0) && px.lt(1) && size.gt(0))
    .sort((l, r) => r[0].cmp(l[0]));
  let left = D(shares);
  let sold = ZERO;
  let cash = ZERO;
  for (const [px, size] of levels) {
    if (left.lte(0)) break;
    const take = Decimal.min(left, size);
    cash = cash.plus(take.times(px.minus(takerFeePerShare(px, feeRate))));
    sold = sold.plus(take);
    left = left.minus(take);
  }
  return { sold, cash };
}

/**
 * Купить у продавцов столько, чтобы на руках после комиссии оказалось `net`
 * шаров. Комиссия покупки удерживается шарами: rate × (1 − p) от каждого.
 */
export function buyNet(
  asks: readonly Level[],
  net: Odds,
  feeRate: Odds = SPORTS_FEE_RATE,
): { got: Decimal; spent: Decimal } {
  const levels = [...asks]
    .map(([px, size]) => [D(px), D(size)] as const)
    .filter(([px, size]) => px.gt(0) && px.lt(1) && size.gt(0))
    .sort((l, r) => l[0].cmp(r[0]));
  let left = D(net);
  let got = ZERO;
  let spent = ZERO;
  for (const [px, size] of levels) {
    if (left.lte(0)) break;
    const keep = new Decimal(1).minus(takerFeePerShare(px, feeRate).div(px));
    const gross = Decimal.min(size, left.div(keep));
    const net = gross.times(keep);
    got = got.plus(net);
    spent = spent.plus(gross.times(px));
    left = left.minus(net);
  }
  return { got, spent };
}

/** Все выходы, лучший по худшему исходу — первым. */
export function exitWays(input: ExitInput): ExitWay[] {
  const fee = input.feeRate ?? SPORTS_FEE_RATE;
  const out: ExitWay[] = [];
  const { stand } = input;

  if (stand.kind === 'shares') {
    const n = D(stand.shares);
    const cost = D(stand.cost);
    if (!n.gt(0)) return [];
    if (input.bids && input.bids.length > 0) {
      const { sold, cash } = sellInto(input.bids, n, fee);
      if (sold.gt(0)) {
        const rest = n.minus(sold);
        out.push(
          way('sell', 'polymarket', sold, cash.div(sold), cash.plus(rest).minus(cost), cash.minus(cost), rest.gt(0)),
        );
      }
    }
    if (input.oppAsks && input.oppAsks.length > 0) {
      const { got, spent } = buyNet(input.oppAsks, n, fee);
      if (got.gt(0)) {
        out.push(
          way(
            'buyOpp',
            'polymarket',
            got,
            spent.div(got),
            n.minus(cost).minus(spent),
            got.minus(cost).minus(spent),
            got.lt(n.minus('0.000001')),
          ),
        );
      }
    }
    const quotes = [
      ...(input.covers ?? []).map((q) => ({ ...q, kind: 'cover' as const })),
      ...(input.retry ? [{ ...input.retry, kind: 'rebet' as const }] : []),
    ];
    for (const q of quotes) {
      const k = D(q.odds);
      if (!k.gt(1)) continue;
      const stake = money(n.div(k));
      out.push(way(q.kind, q.source, stake, k, n.minus(cost).minus(stake), stake.times(k).minus(cost).minus(stake), false));
    }
  } else {
    const s = D(stand.stake);
    const k = D(stand.odds);
    if (!s.gt(0) || !k.gt(1)) return [];
    const back = s.times(k);
    if (input.asks && input.asks.length > 0) {
      const { got, spent } = buyNet(input.asks, back, fee);
      if (got.gt(0)) {
        out.push(
          way('buy', 'polymarket', got, spent.div(got), got.minus(s).minus(spent), back.minus(s).minus(spent), got.lt(back.minus('0.000001'))),
        );
      }
    }
    for (const q of input.covers ?? []) {
      const k2 = D(q.odds);
      if (!k2.gt(1)) continue;
      const stake = money(back.div(k2));
      out.push(way('cover', q.source, stake, k2, stake.times(k2).minus(s).minus(stake), back.minus(s).minus(stake), false));
    }
  }

  return out.sort((l, r) => r.worst.cmp(l.worst) || r.ifA.plus(r.ifB).cmp(l.ifA.plus(l.ifB)));
}

const usd = (v: Decimal): string => `${v.isNegative() ? '−' : '+'}$${v.abs().toFixed(2)}`;

/** Одна строка выхода: «Продай 32 шара по 0.58: −$1.10». */
export function exitSay(w: ExitWay, nameOf: (code: string) => string = (c) => c): string {
  const shares = w.amount.toDecimalPlaces(1, Decimal.ROUND_DOWN).toString();
  const px = w.price.toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toString();
  const result =
    w.ifA.eq(w.ifB) ? usd(w.worst) : `${usd(w.ifA)} / ${usd(w.ifB)}`;
  const tail = w.partial ? ' (стакан не вмещает всё)' : '';
  switch (w.kind) {
    case 'sell':
      return `Продай ${shares} шаров по ${px}: ${result}${tail}`;
    case 'buy':
      return `Докупи ${shares} шаров Polymarket по ${px}: ${result}${tail}`;
    case 'buyOpp':
      return `Купи ${shares} шаров второго исхода на Polymarket по ${px}: ${result}${tail}`;
    case 'rebet':
      return `Доставь $${w.amount.toFixed(2)} в ${nameOf(w.source)} по ${w.price.toFixed(3)}: ${result}`;
    case 'cover':
      return `Перекрой $${w.amount.toFixed(2)} в ${nameOf(w.source)} по ${w.price.toFixed(3)}: ${result}`;
  }
}
